<template>
  <div class="section-container pb-5 charges-calculator container">
    <ol class="breadcrumb arr-right mb-0 mb-md-3 mt-0 mt-md-5">
      <li class="breadcrumb-item">
        <router-link :to="{ name: 'chargesCalculator' }" class="headings">{{
          $t('chargesCalc.index.title')
        }}</router-link>
      </li>
      <li class="breadcrumb-item reference-heading heading-color">
        {{ $t('chargesCalc.index.list') }}
      </li>
    </ol>
    <div v-if="!mainLoader">
      <h5 class="heading-color">{{ $t('chargesCalc.index.desc') }}</h5>
      <b-input-group class="dc-input-group">
        <multiselect
          v-model="selectedValue"
          :options="dropDownData"
          label="label"
          name="selectedInvoice"
          :searchable="true"
          :placeholder="$t('chargesCalc.select.placeHolder')"
          @select="onSelect"
          class="mt-2 some-class"
          @search-change="searchChange"
        >
          <span class="action-input" slot="caret">
            <b-icon-chevron-down />
          </span>
          <span slot="noResult">
            {{ $t('chargesCalc.select.noInvoiceFound') }}
            <span class="font-weight-bold">'{{ this.searchQuery }}'</span>
          </span>
        </multiselect>
      </b-input-group>
    </div>
    <template v-if="!loading">
      <div
        class="d-flex justify-content-center align-items-center flex-column"
        v-if="!selectedValue"
      >
        <b-img
          :src="require('@/assets/images/common/charges-calc.png')"
          class="text-center w-100"
        ></b-img>
        <p>
          {{ $t('chargesCalc.noInvoicesHelpertText') }}
        </p>
      </div>
      <div v-else>
        <invoice-details-table
          :tableItems="tableItems"
          :tableFields="tableFields"
        />
        <calculation-details-table
          :tableBreakupItems="tableBreakupItems"
          :tableBreakupFields="tableBreakupFields"
          :options="options"
          @dateChange="onDateChange"
        />
      </div>
    </template>
    <drip-spinner v-else />
  </div>
</template>

<script>
import InvoiceDetailsTable from '@/components/resources/ChargesCalculator/InvoiceDetailsTable'
import CalculationDetailsTable from '@/components/resources/ChargesCalculator/CalculationDetailsTable'
import invoiceService from '@/services/invoice'
import accountsService from '@/services/dashboard'
import Multiselect from 'vue-multiselect'
import { BIconChevronDown } from 'bootstrap-vue'
import utils from '@/utils/utils'
import { freshdeskMixin } from '@/mixins/Freshdesk'

export default {
  components: {
    Multiselect,
    BIconChevronDown,
    InvoiceDetailsTable,
    CalculationDetailsTable
  },
  data() {
    return {
      mainLoader: true,
      loading: false,
      hasOverdraftLimit: false,
      calcDetailsData: {
        expected_fee_calculation: {
          advances: []
        }
      },
      calcBreakupData: {
        advances: []
      },
      options: {
        format: 'MM/DD/YYYY'
      },
      selectedDate: new Date(),
      searchQuery: '',
      dropdownData: [],
      selectedValue: '',
      tableFields: [
        { key: 'PARTICULARS', thClass: 'bg-secondary text-white b-top-left' },
        { key: 'VALUE', thClass: 'bg-secondary text-white b-top-right' }
      ],
      tableBreakupFields: [
        { key: 'PARTICULARS', thClass: 'bg-secondary text-white b-top-left' },
        {
          key: 'expectedOnDueDate',
          label: 'EXPECTED ON DUE DATE',
          thClass: 'bg-secondary text-white'
        },
        {
          key: 'asOn',
          label: 'AS ON',
          thClass: 'bg-secondary text-white b-top-right'
        }
      ]
    }
  },
  mounted() {
    this.$mpTrack('Charges Calculator - Invoice charges rendered')
    // this.showAndPrefillFreshdesk({})
  },
  created() {
    this.initialFetch()
    this.fetchLimits()
  },
  methods: {
    async initialFetch() {
      this.loading = true
      const invId = this.$route.query.invoiceId
      await this.fetchInvoices()
      if (invId) {
        this.$router.push(this.$route.path)
        this.selectedValue = this.dropDownData.find(
          (el) => el.value === parseInt(invId)
        )
        this.fetchCalculationDetails(invId)
        await this.fetchCalculationBreakup(invId, this.selectedDate)
      }
      this.loading = false
    },
    async fetchLimits() {
      accountsService
        .fetchBusinessAccount({
          include: ['plan']
        })
        .then((res) => {
          this.hasOverdraftLimit = parseInt(res.data.overdraft_limit) > 0
        })
        .catch()
    },
    async fetchInvoices() {
      try {
        const limit = 100
        const serializer = 'Business::InvoiceListSerializer'
        const filterBy = { filters: ['advanced'] }
        const response = await invoiceService.fetch(
          filterBy,
          null,
          limit,
          serializer
        )
        const invoices = response.data['drip/business/invoices']
        this.dropDownData = invoices.map(({ reference, id, currsym }) => ({
          label: reference,
          value: id,
          symbol: currsym
        }))
        this.mainLoader = false
      } catch (e) {}
    },
    async fetchCalculationDetails(invoiceId) {
      try {
        const { data } = await invoiceService.fetchCalculationDetails(invoiceId)
        this.calcDetailsData = data
      } catch (e) {
        this.loading = false
      }
    },
    async fetchCalculationBreakup(invoiceId, date) {
      try {
        const { data } = await invoiceService.fetchCalculationBreakup(
          invoiceId,
          date
        )
        this.$mpTrack(
          'Charges Calculator - Invoice charges data retrieved successfully'
        )
        this.calcBreakupData = data
      } catch (e) {
        this.loading = false
        this.$mpTrack(
          'Error: Charges Calculator - Invoice charges failed to retrieve'
        )
      }
    },
    async onSelect(e) {
      this.loading = true
      this.selectedValue = e
      this.fetchCalculationDetails(this.selectedValue.value)
      this.selectedDate = new Date()
      this.$mpTrack('Charges Calculator - Invoice selected')
      await this.fetchCalculationBreakup(
        this.selectedValue.value,
        this.formatDate(this.selectedDate)
      )
      this.loading = false
    },
    formatDate(str) {
      return this.$dayjs(str).format('DD/MM/YYYY')
    },
    async onDateChange(newDate) {
      this.$mpTrack('Charges Calculator - Predict future Due Date Changed', {
        date: newDate
      })
      await this.fetchCalculationBreakup(
        this.selectedValue.value,
        this.formatDate(newDate)
      )
    },
    formatCurrency(value, symbol) {
      return this.$options.filters.currency(value, symbol, ',', 2)
    },
    blueValue(value) {
      if (value) {
        return `<div class="text-secondary font-weight-bold">${value}</div>`
      }
    },
    searchChange(value) {
      this.searchQuery = value
    }
  },
  computed: {
    tableItems: function () {
      const particulars = [
        {
          PARTICULARS: this.$t('chargesCalc.table.value'),
          VALUE: this.formatCurrency(
            this.calcDetailsData.value,
            this.selectedValue.symbol
          )
        },
        {
          PARTICULARS: this.$t('chargesCalc.table.advanceTotal'),
          VALUE: this.formatCurrency(
            this.calcDetailsData.advance_total,
            this.selectedValue.symbol
          )
        },
        {
          PARTICULARS: this.$t('chargesCalc.table.depositTotal'),
          VALUE: this.formatCurrency(
            this.calcDetailsData.deposit_total,
            this.selectedValue.symbol
          )
        },
        {
          PARTICULARS: this.$t('chargesCalc.table.disbursedDate'),
          VALUE: utils.dateFormatterTable(this.calcDetailsData.disbursed_date)
        },
        {
          PARTICULARS: this.$t('chargesCalc.table.dueDate'),
          VALUE: utils.dateFormatterTable(this.calcDetailsData.due_date)
        },
        {
          PARTICULARS: this.$t('chargesCalc.table.receivedDate'),
          VALUE: utils.dateFormatterTable(this.calcDetailsData.received_date)
        },
        {
          PARTICULARS: this.$t('chargesCalc.table.receivedAmount'),
          VALUE: this.formatCurrency(
            this.calcDetailsData.received_amount,
            this.selectedValue.symbol
          )
        }
      ]
      if (this.hasOverdraftLimit) {
        particulars.push({
          PARTICULARS: this.$t('chargesCalc.table.overdraftLimitUtilized'),
          VALUE: this.formatCurrency(
            this.calcDetailsData.overdraft_limit_utilized,
            this.selectedValue.symbol
          )
        })
      }
      return particulars
    },
    tableBreakupItems: function () {
      const advanceRows = []
      this.calcDetailsData.expected_fee_calculation.advances.forEach(
        (el, index) => {
          advanceRows.push({
            PARTICULARS: `Advance #${index + 1} ${this.$t(
              'chargesCalc.calcDetailsTable.advanceDuration'
            )}`,
            expectedOnDueDate: el.duration,
            asOn: this.calcBreakupData.advances[index]?.duration
          })
          advanceRows.push({
            PARTICULARS: `Advance #${index + 1} ${this.$t(
              'chargesCalc.calcDetailsTable.advanceRegularInterest'
            )}`,
            expectedOnDueDate: this.formatCurrency(
              el.regular_interest,
              this.selectedValue.symbol
            ),
            asOn: this.formatCurrency(
              this.calcBreakupData.advances[index]?.regular_interest,
              this.selectedValue.symbol
            )
          })
        }
      )
      //
      const isDBRExists =
        (this.calcBreakupData && !!this.calcBreakupData.step_up_dbr_interest) ||
        (this.calcDetailsData &&
          this.calcDetailsData.expected_fee_calculation &&
          !!this.calcDetailsData.expected_fee_calculation.step_up_dbr_interest)

      // Add rows for Step up duration in DBR
      const currentStepUpDbr =
        this.calcDetailsData.expected_fee_calculation.step_up_dbr_duration || []
      const asOnStepUpDbr = this.calcBreakupData.step_up_dbr_duration || []
      const stepUpDbrRows = currentStepUpDbr.reduce((rows, current, index) => {
        const asOnDays = asOnStepUpDbr[index] ? asOnStepUpDbr[index].days : 0
        const asOnRate = asOnStepUpDbr[index] ? asOnStepUpDbr[index].rate : 0
        const daysRow = {
          PARTICULARS: this.$t(
            'chargesCalc.calcDetailsTable.stepUpDbrDuration',
            {
              number: index + 1
            }
          ),
          expectedOnDueDate: current.days,
          asOn: asOnDays
        }
        const rateRow = {
          PARTICULARS: this.$t('chargesCalc.calcDetailsTable.stepUpDbrRate', {
            number: index + 1
          }),
          expectedOnDueDate: `${current.rate}%`,
          asOn: `${asOnRate}%`
        }
        return isDBRExists ? rows.concat(daysRow, rateRow) : []
      }, [])
      const particulars = [
        {
          PARTICULARS: this.blueValue(
            this.$t('chargesCalc.calcDetailsTable.duration')
          ),
          expectedOnDueDate:
            this.calcDetailsData.expected_fee_calculation.duration,
          asOn: this.calcBreakupData.duration
        },
        ...stepUpDbrRows,
        {
          PARTICULARS: this.$t('chargesCalc.calcDetailsTable.overdue'),
          expectedOnDueDate:
            this.calcDetailsData.expected_fee_calculation.overdue,
          asOn: this.calcBreakupData.overdue
        },
        {
          PARTICULARS: this.$t('chargesCalc.calcDetailsTable.factoringRate'),
          expectedOnDueDate:
            this.calcDetailsData.expected_fee_calculation.interest_rate + '%',
          asOn: this.calcBreakupData.interest_rate + '%'
        }
      ]

      if (this.hasOverdraftLimit) {
        particulars.push({
          PARTICULARS: this.$t(
            'chargesCalc.calcDetailsTable.overDraftPremiumTransactionFee'
          ),
          expectedOnDueDate:
            this.calcDetailsData.expected_fee_calculation
              .overdraft_transaction_rate + '%',
          asOn: this.calcBreakupData.overdraft_transaction_rate + '%'
        })
      }

      particulars.push({
        PARTICULARS: this.blueValue(
          this.$t('chargesCalc.calcDetailsTable.transactionFee')
        ),
        expectedOnDueDate: this.formatCurrency(
          this.calcDetailsData.expected_fee_calculation.factoring_fees,
          this.selectedValue.symbol
        ),
        asOn: this.formatCurrency(
          this.calcBreakupData.factoring_fees,
          this.selectedValue.symbol
        )
      })

      if (this.hasOverdraftLimit) {
        particulars.push({
          PARTICULARS: this.$t(
            'chargesCalc.calcDetailsTable.overDraftTransactionfee'
          ),
          expectedOnDueDate: this.formatCurrency(
            this.calcDetailsData.expected_fee_calculation
              .overdraft_transaction_fee,
            this.selectedValue.symbol
          ),
          asOn: this.formatCurrency(
            this.calcBreakupData.overdraft_transaction_fee,
            this.selectedValue.symbol
          )
        })
      }

      const otherParticular = [
        ...advanceRows,
        {
          PARTICULARS: this.$t('chargesCalc.calcDetailsTable.regularInterest'),
          expectedOnDueDate: this.formatCurrency(
            this.calcDetailsData.expected_fee_calculation.regular_interest,
            this.selectedValue.symbol
          ),
          asOn: this.formatCurrency(
            this.calcBreakupData.regular_interest,
            this.selectedValue.symbol
          )
        },
        {
          PARTICULARS: this.$t('chargesCalc.calcDetailsTable.overdueInterest'),
          expectedOnDueDate: this.formatCurrency(
            this.calcDetailsData.expected_fee_calculation.overdue_interest,
            this.selectedValue.symbol
          ),
          asOn: this.formatCurrency(
            this.calcBreakupData.overdue_interest,
            this.selectedValue.symbol
          )
        },
        {
          PARTICULARS: this.blueValue(
            this.$t(
              `chargesCalc.calcDetailsTable.${
                isDBRExists ? 'totalInterest' : 'totalInterestWithoutDBR'
              }`
            )
          ),
          expectedOnDueDate: this.formatCurrency(
            this.calcDetailsData.expected_fee_calculation.total_interest,
            this.selectedValue.symbol
          ),
          asOn: this.formatCurrency(
            this.calcBreakupData.total_interest,
            this.selectedValue.symbol
          )
        },
        {
          PARTICULARS: this.$t('chargesCalc.calcDetailsTable.lateFee'),
          expectedOnDueDate: this.formatCurrency(
            this.calcDetailsData.expected_fee_calculation.late_fee,
            this.selectedValue.symbol
          ),
          asOn: this.formatCurrency(
            this.calcBreakupData.late_fee,
            this.selectedValue.symbol
          )
        },
        {
          PARTICULARS: this.$t('chargesCalc.calcDetailsTable.docFee'),
          expectedOnDueDate: this.formatCurrency(
            this.calcDetailsData.expected_fee_calculation.doc_fee,
            this.selectedValue.symbol
          ),
          asOn: this.formatCurrency(
            this.calcBreakupData.doc_fee,
            this.selectedValue.symbol
          )
        },
        {
          PARTICULARS: this.$t('chargesCalc.calcDetailsTable.processingFee'),
          expectedOnDueDate: this.formatCurrency(
            this.calcDetailsData.expected_fee_calculation.processing_fee,
            this.selectedValue.symbol
          ),
          asOn: this.formatCurrency(
            this.calcBreakupData.processing_fee,
            this.selectedValue.symbol
          )
        },
        {
          PARTICULARS: this.$t('chargesCalc.calcDetailsTable.adjustments'),
          expectedOnDueDate: this.formatCurrency(
            this.calcDetailsData.expected_fee_calculation.adjustments,
            this.selectedValue.symbol
          ),
          asOn: this.formatCurrency(
            this.calcBreakupData.adjustments,
            this.selectedValue.symbol
          )
        },
        {
          PARTICULARS: this.$t('chargesCalc.calcDetailsTable.otherFees'),
          expectedOnDueDate: this.formatCurrency(
            this.calcDetailsData.expected_fee_calculation,
            this.selectedValue.symbol
          ),
          asOn: this.formatCurrency(
            this.calcBreakupData.other_fees,
            this.selectedValue.symbol
          )
        },
        {
          PARTICULARS: this.blueValue(
            this.hasOverdraftLimit
              ? this.$t('chargesCalc.calcDetailsTable.totalFees')
              : this.$t('chargesCalc.calcDetailsTable.totalFeesOD')
          ),
          expectedOnDueDate: this.blueValue(
            this.formatCurrency(
              this.calcDetailsData.expected_fee_calculation.total_fees,
              this.selectedValue.symbol
            )
          ),
          asOn: this.blueValue(
            this.formatCurrency(
              this.calcBreakupData.total_fees,
              this.selectedValue.symbol
            )
          )
        },
        {
          PARTICULARS: this.blueValue(
            this.$t('chargesCalc.calcDetailsTable.totalSettlement')
          ),
          expectedOnDueDate: this.blueValue(
            this.formatCurrency(
              this.calcDetailsData.expected_fee_calculation.collection_amount,
              this.selectedValue.symbol
            )
          ),
          asOn: this.blueValue(
            this.formatCurrency(
              this.calcBreakupData.collection_amount,
              this.selectedValue.symbol
            )
          )
        }
      ]

      if (isDBRExists) {
        otherParticular.splice(advanceRows.length + 2, 0, {
          PARTICULARS: this.$t(
            'chargesCalc.calcDetailsTable.stepUpDbrInterest'
          ),
          expectedOnDueDate: this.formatCurrency(
            this.calcDetailsData.expected_fee_calculation.step_up_dbr_interest,
            this.selectedValue.symbol
          ),
          asOn: this.formatCurrency(
            this.calcBreakupData.step_up_dbr_interest,
            this.selectedValue.symbol
          )
        })
      }
      return particulars.concat(otherParticular)
    }
  },
  mixins: [freshdeskMixin]
}
</script>
<style lang="scss">
@import '~vue-multiselect/dist/vue-multiselect.min.css';
.charges-calculator {
  flex-direction: column;
  .action-input {
    position: absolute;
    cursor: pointer;
    right: 0;
    padding-left: 0.67rem;
    padding-right: 0.67rem;
    line-height: 2.67rem;
    background: $noble-blue-500;
    color: $white;
    font-size: 1.2rem;
    border-bottom-right-radius: 5px;
    border-top-right-radius: 5px;
    top: 0;
  }
  .multiselect__option--highlight {
    background: $noble-blue-500;
    &:after {
      background: $noble-blue-500;
    }
  }
  .multiselect__tags {
    height: 43px;
  }
  .header-cell {
    color: rgb(255, 255, 255);
  }
  .heading-desc {
    font-size: 20px;
    font-weight: 600;
  }
  .heading-caption {
    font-size: 12px;
  }
  .invoice-calculation {
    width: 523px;
    height: 349px;
  }
  .field-wrap {
    padding: 15px 20px !important;
    border: 1px solid #eaebf0 !important;
  }
  .card-body {
    padding: 0px 0px 0px 0px;
  }
  .card {
    border-radius: 5px;
  }
  .container-box {
    margin-bottom: 180px;
  }
  .table {
    margin-bottom: 0rem;
  }
  .date-picker-container {
    .mx-datepicker {
      width: 100%;
    }
    .mx-input {
      color: $dc-input-color;
      font-weight: bold;
      border: 1px solid $dc-input-border-color;
      min-height: 2.67rem;
      box-shadow: none;
    }
  }
  .calc-table {
    table-layout: fixed;
    th,
    td {
      border: 1px solid #eaebf0;
      padding: 1.25rem 2rem;
      @media screen and (max-width: $tablet) {
        padding: 0.5rem;
      }
      vertical-align: middle;
    }
    thead {
      th,
      td {
        border-bottom-width: (2 * 1px);
      }
    }
    .list-unstyled {
      border: 0;
      th,
      td {
        border: 0;
        padding: 0.5em;
      }
      thead {
        th,
        td {
          border-bottom-width: 0;
        }
      }
    }
  }
  .va-middle {
    vertical-align: middle;
  }
  .b-top-right {
    border-top-right-radius: 5px;
  }
  .b-top-left {
    border-top-left-radius: 5px;
  }
}
@include media-breakpoint-down(sm) {
  .action-input {
    border-radius: 5px;
    width: 40px;
  }
}
</style>
